import { useMutation } from "@apollo/client/react";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import {
  GetPropertySchemaSetDocument,
  GetRecordMetadataPropertySchemaSetsDocument,
  UpdateRecordMetadataSchemaDocument,
  type UpdateRecordMetadataSchemaMutation,
  type UpdateRecordMetadataSchemaMutationVariables,
} from "~/features/admin/recordMetadataSchema/gqls";
import type { RecordMetadataSchemaFormData } from "~/features/admin/recordMetadataSchema/types";
import { toPropertySchemaAssociationInputList } from "~/features/admin/recordMetadataSchema/utils/transformers";
import { useTenant } from "~/features/tenant/context";
import { translateError } from "~/lib/errors";

interface UseRecordMetadataSchemaEditOptions {
  onSuccess?: () => void;
}

export function useRecordMetadataSchemaEdit({ onSuccess }: UseRecordMetadataSchemaEditOptions = {}) {
  const { tenantId } = useTenant();
  if (!tenantId) {
    throw new Error("Tenant ID is required");
  }
  const { t } = useTranslation();
  const [processing, setProcessing] = useState(false);
  const [updateRecordMetadataSchema] = useMutation<
    UpdateRecordMetadataSchemaMutation,
    UpdateRecordMetadataSchemaMutationVariables
  >(UpdateRecordMetadataSchemaDocument);

  const submit = useCallback(
    async (formData: RecordMetadataSchemaFormData) => {
      const id = formData.id;
      setProcessing(true);
      try {
        await updateRecordMetadataSchema({
          refetchQueries: [
            { query: GetRecordMetadataPropertySchemaSetsDocument },
            { query: GetPropertySchemaSetDocument, variables: { id } },
          ],
          variables: {
            input: {
              tenantId,
              id,
              propertySchemas: toPropertySchemaAssociationInputList(formData.propertySchemaAssociations ?? []),
            },
          },
        });

        toast.success(t("feature.common.updatedSuccessfully", { model: "recordMetadataSchema" }));
        onSuccess?.();
      } catch (e) {
        toast.error(translateError(t, e));
      } finally {
        setProcessing(false);
      }
    },
    [updateRecordMetadataSchema, tenantId, t, onSuccess],
  );

  return { submit, processing };
}
